'use client'

import { useState } from 'react'
import Link from 'next/link'
import { supabase } from '@/lib/supabase'
import { Navbar } from '@/components/custom/main/navbar'
import { SiteFooter } from '@/components/custom/main/footer'

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const redirectTo = `${process.env.NEXT_PUBLIC_AUTH_REDIRECT_URL || 'https://dev.goal-archive.net'}/reset-password`
    const { error: authError } = await supabase.auth.resetPasswordForEmail(email, { redirectTo })

    setLoading(false)
    if (authError) {
      setError(authError.message)
      return
    }
    setSent(true)
  }

  if (sent) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: '#000000' }}>
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4 py-12 pt-20">
          <div className="w-full max-w-sm text-center">
            <div className="mb-6">
              <div
                className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-white/10"
                style={{ background: '#0e1114' }}
              >
                <svg className="h-5 w-5 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
              </div>
              <h2 className="text-base font-semibold text-white">Check your email</h2>
              <p className="mt-2 text-sm text-white/45 leading-relaxed">
                If an account exists for <span className="text-white/70">{email}</span>, we sent a link to reset your password.
              </p>
            </div>
            <Link href="/signin" className="text-sm text-white/50 hover:text-white transition-colors no-underline">
              Back to sign in
            </Link>
          </div>
        </div>
        <SiteFooter />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#000000' }}>
      <Navbar />

      <div className="flex-1 flex items-center justify-center px-4 py-12 pt-20">
        <div className="w-full max-w-sm">
          <div className="mb-8 text-center">
            <h1 className="text-base font-semibold text-white">Reset password</h1>
            <p className="mt-1.5 text-sm text-white/40">We'll email you a link to reset it</p>
          </div>

          <div className="rounded-lg border border-white/8 px-6 py-7" style={{ background: '#0a0c0e' }}>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-xs font-medium text-white/50 uppercase tracking-wider">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/25 outline-none focus:border-white/25 focus:bg-white/8 transition-colors"
                  placeholder="you@example.com"
                />
              </div>

              {error && (
                <div className="rounded-md border border-red-500/20 bg-red-500/8 px-3 py-2.5">
                  <p className="text-xs text-red-400 leading-relaxed">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="mt-1 w-full rounded-md bg-white py-2.5 text-sm font-semibold text-black transition-opacity disabled:opacity-50 hover:opacity-90"
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
          </div>

          <p className="mt-5 text-center text-sm text-white/35">
            <Link href="/signin" className="text-white/70 hover:text-white transition-colors no-underline">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>

      <SiteFooter />
    </div>
  )
}
